import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateLongTermParkersRequest, OrganizationService, ParkingTypeService, ParkingSpaceDto, OrganizationDto, ListOrganizationRequest, ParkingZoneDto } from '../../../../../api/gen';

export interface LongTermTypeResult {
  success: boolean;
  data?: any;
}

@Component({
  selector: 'app-add-long-term-type',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './add-long-term-type.component.html',
  styleUrl: './add-long-term-type.component.scss'
})
export class AddLongTermTypeComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingTypeService = inject(ParkingTypeService);
  private organizationService = inject(OrganizationService);
  private snackBar = inject(MatSnackBar);

  @Input({ required: true }) parkingOperatorId: string | undefined;
  @Input() parkingSpace!: ParkingSpaceDto;
  @Output() submitted = new EventEmitter<LongTermTypeResult>();

  form!: FormGroup;
  isLoading = signal(false);
  isLoadingOrgs = signal(false);
  availableOrganizations = signal<OrganizationDto[]>([]);
  availableZones = signal<ParkingZoneDto[]>([]);

  ngOnInit(): void {
    this.initForm();
    this.loadOrganizations();
    this.availableZones.set(this.parkingSpace?.parkingZones ?? []);
  }

  private initForm(): void {
    this.form = this.fb.group({
      organizationId: [null],
      availableForParkingZones: [[] as string[]],
      availableFrom: [null],
      availableTo: [null]
    });
  }

  private loadOrganizations(): void {
    this.isLoadingOrgs.set(true);
    const request: ListOrganizationRequest = {
      pageSize: 100,
      pageIndex: 0
    };
    this.organizationService.organizationsFilterPost(request).subscribe({
      next: (result) => {
        this.availableOrganizations.set(result.items || []);
        this.isLoadingOrgs.set(false);
      },
      error: () => {
        this.isLoadingOrgs.set(false);
      }
    });
  }

  submit(): void {
    this.isLoading.set(true);

    const formValue = this.form.value;
    const zoneIds: string[] = formValue.availableForParkingZones ?? [];

    const request: CreateLongTermParkersRequest = {
      availableFrom: formValue.availableFrom ? new Date(formValue.availableFrom).toISOString() : null,
      availableTo: formValue.availableTo ? new Date(formValue.availableTo).toISOString() : null,
      availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : null,
      // Null, not an empty list, when nothing is picked: null means "the whole space", which is the
      // shape every existing long-term type has. Which zones a parker may actually use is decided by
      // their parking right, not by the type — this only limits where the type is offered at all.
      availableForParkingZones: zoneIds.length > 0 ? zoneIds : null,
      organizationId: formValue.organizationId || null
    };

    this.parkingTypeService.parkingTypesLongTermParkersPost(this.parkingOperatorId, request).subscribe({
      next: (result) => {
        this.snackBar.open('Long-Term type created successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: true, data: result });
      },
      error: (error) => {
        console.error('Error creating long-term type:', error);
        this.snackBar.open('Failed to create long-term type', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: false });
      }
    });
  }
}
